import { Grid, Card, CardHeader, CardContent, TextField, FormGroup, FormControlLabel, FormHelperText, Switch, SxProps, Theme, alpha, emphasize, useTheme } from "@mui/material";
import { useRecordContext } from "ra-core";
import { FC, useEffect } from "react";
import { InfoTypeInput, InfoTypeResponseDto } from "@/gen/api/secureai";
import { usePlainStateFormUtils } from "@/components/input/usePlainStateFormUtils";
import { SecAIInfoTypeDataContextProvider } from "./SecAIInfoTypeDataProvider";
import { useZSliceUnderContext } from '@/utils/zustand-form/hooks';
import { useZTreeStoreContext } from '@/utils/zustand-form/store.context';
import { CollectEditsMetadata, CollectEditsProtocol } from '@/utils/zustand-form/ext/collectEdits';

import DetectPatternEditor from './DetectPatternEditor';
import DenyListEditor from './DenyListEditor';
import KeywordRuleEditor from './KeywordRuleEditor';
import ExclusionRuleEditor from './ExclusionRuleEditor';
import { topSchema, emptyInfoTypeDto } from './InfoTypeEditor.types';

const InfoTypeEditor : FC<{
  title: string,
  action?: React.ReactNode,
}> = ({ title, action }) => {
  const record = useRecordContext<InfoTypeResponseDto>() ?? emptyInfoTypeDto
  const createMode = record === emptyInfoTypeDto
  const isBuiltIn = record.isBuiltIn

  // Get zstore from context (parent provides it)
  const zstore = useZTreeStoreContext();
  const utils = useZSliceUnderContext({
    store: zstore,
    prevData: record,
    stageInit: () => ({ name: record.name, displayName: record.displayName, description: record.description, isEnabled: record.isEnabled }),
    extraStates: { denyListCount: 0, detectPatternCount: 0, countTouched: false },
  });
  const { states, setStates, token, installProtocolMetadata } = utils;
  const { trySubmitValidate, fieldProps } = usePlainStateFormUtils(states, setStates, topSchema);

  useEffect(() => {
    installProtocolMetadata<CollectEditsMetadata>(CollectEditsProtocol.metadataKey, {
      triggerValidate: async () => {
        if (isBuiltIn) { 
          // automatically resolved, because only 'enable/disable' is editable
          return
        }
        await trySubmitValidate()
      },
      overrideCollect: async (node, safeInvokeNode, safeChildrenCollection) => {
        if (isBuiltIn) {
          // For built-in, only return the stage (no child aggregation needed)
          return { ...node.state.stage };
        }
        const edits : InfoTypeInput = {...node.state.stage}
        for (const k of safeChildrenCollection.keys()) {
          const childNode = safeChildrenCollection.get(k)!
          const childEdits = await safeInvokeNode(childNode)
          const k2 : keyof InfoTypeInput = k as any;
          (edits[k2] as any) = childEdits
        }
        const v1 = node.state
        const v2 = {...v1, 
          denyListCount: edits.denyList?.length ?? 0, 
          detectPatternCount: edits.detectPatterns?.length ?? 0, 
          countTouched: true
        }
        if (!isBuiltIn && (v2.denyListCount + v2.detectPatternCount < 1)) {
          v2.errors['leastRuleCount'] = 'Should define at least 1 rule in either Keyword Detection or Pattern Detection'
        }
        setStates(v2)
        if (Object.keys(v2.errors).length > 0)  {
          throw new Error('Validation error', { cause: v2.errors })
        }
        return edits
      },
    });
  }, [installProtocolMetadata, isBuiltIn, setStates, states.stage, trySubmitValidate]);

  const theme = useTheme()
  const colorAdj = (theme: Theme) => {
    const tscolor = theme.palette.text.secondary
    return `${theme.palette.mode === 'light' ? tscolor : emphasize(tscolor)} !important`
  }
  const disabledProps = (x:boolean|undefined) => x ? { 
    fontStyle: 'italic', 
    '& textarea,input,.MuiInputBase-root,.Mui-disabled': {
      color: colorAdj,
      WebkitTextFillColor: colorAdj,
    }
  } as SxProps<Theme> : undefined
  return (
    <Card {...(theme.palette.mode === 'dark' && {
      sx: {
        bgcolor: '#121b25',
        '& div.MuiInputBase-root .MuiChip-root': {
          bgcolor: 'rgb(255 255 255 / 6%)',
        }
      }
    })}>
      <CardHeader title={title}
        action={action}
      />
      <SecAIInfoTypeDataContextProvider>
        <CardContent>
          <Grid container spacing={4}>
            <Grid item xs={12}>
              <FormGroup sx={{ mb: 1 }}>
                <FormControlLabel
                  label="Enable Info Type"
                  control={
                    <Switch checked={states.stage.isEnabled}
                      onChange={(e, isEnabled) => setStates(v => ({...v, stage: {...v.stage, isEnabled },
                        touched: {...v.touched, isEnabled: isEnabled !== v.prevData.isEnabled } as any,
                      }))} />
                  }
                />
              </FormGroup>
            </Grid>
            <Grid item xs={6}>
              <TextField label="Name*" disabled={!createMode || isBuiltIn} sx={disabledProps(!createMode || isBuiltIn)} multiline fullWidth {...fieldProps('name')}/>
            </Grid>
            <Grid item xs={6}>
              <TextField label="Display Name*" disabled={isBuiltIn} sx={disabledProps(isBuiltIn)} fullWidth multiline {...fieldProps('displayName')}/>
            </Grid>
            <Grid item xs={12}>
              <TextField label="Description" disabled={isBuiltIn} sx={disabledProps(isBuiltIn)} fullWidth multiline {...fieldProps('description')}/>
            </Grid>
            {!isBuiltIn && states.countTouched && (states.denyListCount + states.detectPatternCount < 1) &&
              <Grid item xs={12}>
                <FormHelperText error>Should define at least 1 rule in either Keyword Detection or Pattern Detection</FormHelperText>
              </Grid>}
            {!isBuiltIn &&
              <>
                <Grid item xs={12}>
                  <DenyListEditor parentToken={token} baseData={states.prevData.denyList!} />
                </Grid>
                <Grid item xs={12}>
                  <DetectPatternEditor parentToken={token} baseData={states.prevData.detectPatterns!} />
                </Grid>
                <Grid item xs={12}>
                  <KeywordRuleEditor parentToken={token} baseData={states.prevData.keywordRules!} />
                </Grid>
                <Grid item xs={12}>
                  <ExclusionRuleEditor parentToken={token} baseData={states.prevData.exclusionRules!} />
                </Grid>
              </>
            }

          </Grid>
        </CardContent>
      </SecAIInfoTypeDataContextProvider>
    </Card>
  )
}

export default InfoTypeEditor;
